import { ResumableUploadError } from "./ResumableUploadError";

const MAX_NETWORK_ATTEMPTS = 3;
const MIN_CHUNK_REQUEST_TIMEOUT_MS = 120_000;
const STATUS_REQUEST_TIMEOUT_MS = 30_000;
const MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND = 16 * 1024;
const CHUNK_REQUEST_TIMEOUT_BUFFER_MS = 30_000;

function chunkRequestTimeoutMs(chunkBytes: number): number {
  const transferTimeMs = Math.ceil(
    (chunkBytes / MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND) * 1000,
  );
  return Math.max(
    MIN_CHUNK_REQUEST_TIMEOUT_MS,
    transferTimeMs + CHUNK_REQUEST_TIMEOUT_BUFFER_MS,
  );
}

type SendRequest = {
  readonly request: XMLHttpRequest;
  readonly uploadUrl: string;
  readonly body: Blob;
  readonly offset: number;
  readonly totalBytes: number;
  readonly onProgress?: (loaded: number) => void;
};

type UploadBlobParams = {
  readonly uploadUrl: string;
  readonly file: File;
  readonly chunkSize: number;
  readonly createRequest: () => XMLHttpRequest;
  readonly onProgress?: (uploadedBytes: number) => void;
  readonly retryDelay?: (attempt: number) => Promise<void>;
};

function defaultRetryDelay(attempt: number): Promise<void> {
  const exponentialDelay = Math.min(2000, 100 * 2 ** (attempt - 1));
  const jitter = Math.floor(Math.random() * 100);
  return new Promise((resolve) => {
    window.setTimeout(resolve, exponentialDelay + jitter);
  });
}

function confirmedOffset(request: XMLHttpRequest, fallback: number): number {
  const range = request.getResponseHeader("Range");
  const match = range?.match(/bytes=0-(\d+)/);
  return match ? Number(match[1]) + 1 : fallback;
}

function isTransientHttpStatus(status: number): boolean {
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

function sendChunk(input: SendRequest): Promise<number> {
  const end = input.offset + input.body.size - 1;
  input.request.open("PUT", input.uploadUrl, true);
  input.request.setRequestHeader("Content-Type", "application/octet-stream");
  input.request.setRequestHeader(
    "Content-Range",
    `bytes ${input.offset}-${end}/${input.totalBytes}`,
  );
  input.request.timeout = chunkRequestTimeoutMs(input.body.size);

  return new Promise((resolve, reject) => {
    input.request.upload.onprogress = (event) => {
      if (event.lengthComputable) input.onProgress?.(event.loaded);
    };
    const rejectTransientFailure = () => {
      reject(
        new ResumableUploadError(
          "UPLOAD_NETWORK_ERROR",
          "La conexión se interrumpió durante la carga",
          input.request.status || undefined,
        ),
      );
    };
    input.request.onerror = rejectTransientFailure;
    input.request.ontimeout = rejectTransientFailure;
    input.request.onabort = rejectTransientFailure;
    input.request.onload = () => {
      if (input.request.status === 200 || input.request.status === 201) {
        resolve(input.totalBytes);
        return;
      }
      if (input.request.status === 308) {
        resolve(confirmedOffset(input.request, input.offset));
        return;
      }
      if (isTransientHttpStatus(input.request.status)) {
        rejectTransientFailure();
        return;
      }
      reject(
        new ResumableUploadError(
          "UPLOAD_TRANSPORT_ERROR",
          "El almacenamiento rechazó una parte del archivo",
          input.request.status,
        ),
      );
    };
    input.request.send(input.body);
  });
}

function queryConfirmedOffset(
  request: XMLHttpRequest,
  uploadUrl: string,
  totalBytes: number,
): Promise<number> {
  request.open("PUT", uploadUrl, true);
  request.setRequestHeader("Content-Range", `bytes */${totalBytes}`);
  request.timeout = STATUS_REQUEST_TIMEOUT_MS;

  return new Promise((resolve, reject) => {
    const rejectTransientFailure = () => {
      reject(
        new ResumableUploadError(
          "UPLOAD_NETWORK_ERROR",
          "No pudimos consultar el avance de la carga",
          request.status || undefined,
        ),
      );
    };
    request.onerror = rejectTransientFailure;
    request.ontimeout = rejectTransientFailure;
    request.onabort = rejectTransientFailure;
    request.onload = () => {
      if (request.status === 200 || request.status === 201) {
        resolve(totalBytes);
        return;
      }
      if (request.status === 308) {
        resolve(confirmedOffset(request, 0));
        return;
      }
      if (isTransientHttpStatus(request.status)) {
        rejectTransientFailure();
        return;
      }
      reject(
        new ResumableUploadError(
          "UPLOAD_STATUS_ERROR",
          "No pudimos recuperar el avance de la carga",
          request.status,
        ),
      );
    };
    request.send(null);
  });
}

async function recoverConfirmedOffset({
  uploadUrl,
  totalBytes,
  createRequest,
  retryDelay,
  networkAttempts,
}: {
  uploadUrl: string;
  totalBytes: number;
  createRequest: () => XMLHttpRequest;
  retryDelay: (attempt: number) => Promise<void>;
  networkAttempts: number;
}): Promise<{ offset: number; networkAttempts: number }> {
  let attempts = networkAttempts;
  while (attempts < MAX_NETWORK_ATTEMPTS) {
    await retryDelay(attempts);
    try {
      return {
        offset: await queryConfirmedOffset(
          createRequest(),
          uploadUrl,
          totalBytes,
        ),
        networkAttempts: attempts,
      };
    } catch (error: unknown) {
      if (
        !(error instanceof ResumableUploadError) ||
        error.code !== "UPLOAD_NETWORK_ERROR"
      ) {
        throw error;
      }
      attempts += 1;
      if (attempts >= MAX_NETWORK_ATTEMPTS) throw error;
    }
  }
  throw new ResumableUploadError(
    "UPLOAD_NETWORK_ERROR",
    "No pudimos recuperar el avance de la carga",
  );
}

export async function uploadBlobResumably({
  uploadUrl,
  file,
  chunkSize,
  createRequest,
  onProgress,
  retryDelay = defaultRetryDelay,
}: UploadBlobParams): Promise<void> {
  let offset = 0;
  let networkAttempts = 0;

  while (offset < file.size) {
    const body = file.slice(offset, Math.min(offset + chunkSize, file.size));
    try {
      const nextOffset = await sendChunk({
        request: createRequest(),
        uploadUrl,
        body,
        offset,
        totalBytes: file.size,
        onProgress: (loaded) => onProgress?.(offset + loaded),
      });
      if (nextOffset <= offset) {
        throw new ResumableUploadError(
          "UPLOAD_NETWORK_ERROR",
          "No pudimos confirmar el avance de la carga",
        );
      }
      offset = nextOffset;
      networkAttempts = 0;
    } catch (error: unknown) {
      if (
        !(error instanceof ResumableUploadError) ||
        error.code !== "UPLOAD_NETWORK_ERROR" ||
        networkAttempts >= MAX_NETWORK_ATTEMPTS - 1
      ) {
        throw error;
      }
      networkAttempts += 1;
      const recovered = await recoverConfirmedOffset({
        uploadUrl,
        totalBytes: file.size,
        createRequest,
        retryDelay,
        networkAttempts,
      });
      const previousOffset = offset;
      offset = recovered.offset;
      networkAttempts =
        recovered.offset > previousOffset ? 0 : recovered.networkAttempts;
      onProgress?.(offset);
    }
  }
}
